import {useEffect, useMemo} from "react";
import {useMessages} from "i18n";
import {signInWithToken, authenticatedSessionUid} from "../auth/authProviders";
import {LoginModal, type LoginModalNote} from "../login/LoginModal";
import {SignupLinkCard} from "../login/SignupLinkCard";
import {stopReminders} from "../login/stopReminders";
import {useEmailCodeLogin} from "../login/useEmailCodeLogin";
import {useSignupLink} from "../login/useSignupLink";
import {useAuthenticatedSessionSnapshot} from "../UserProvider";
import {AuroraBackdrop, PageBackdrop} from "../theme/backdrops";
import {goToRoot, useHistory} from "../utils/HistoryUtils";
import {Shell} from "./LoginPage.styles";

/**
 * Public /login route (pre-rendered): the same v3 login-modal experience as the in-place modal
 * (see `login/LoginModalProvider`), presented over the dotted-paper backdrop. Dismissing or
 * completing the login lands on the app root, preserving the previous full-page flow's post-login
 * navigation.
 *
 * It is ALSO where the unfinished-signup reminder's two links land (ui#1688), which is why this route
 * has a second face. The tokens ride in the URL fragment, so they never reach a server log and the
 * path stays static — no CloudFront SPA prefix entry, exactly like `/unsubscribe`.
 *
 * The reminder card replaces the modal only AFTER hydration: this page is pre-rendered with no
 * fragment, so the first client render has to be the modal or the served HTML and the client tree
 * disagree. `useSignupLink` reads the fragment in a layout effect, so the swap happens before the
 * browser paints and a reader arriving on a reminder link never sees the modal flash past.
 */
export default function LoginPage() {
	const history = useHistory();
	const {login} = useMessages();
	const leave = () => goToRoot(history);
	const [authenticatedSession, readLatestSession] = useAuthenticatedSessionSnapshot();
	// A second `useEmailCodeLogin` beside the modal's own, and deliberately so: the two are independent
	// attempts. The modal's belongs to a typed code, this one to the link, and the link's redemption has
	// to survive the modal being unmounted the moment the card replaces it.
	const codeLogin = useEmailCodeLogin(signInWithToken, authenticatedSession, readLatestSession, authenticatedSessionUid);
	const {state, confirm, stop} = useSignupLink({
		finishByLink: codeLogin.finishByLink,
		stopReminders,
		authenticatedSession: authenticatedSession.state,
	});

	// The session is the authority for "you are in", so the same effect carries both ways of getting
	// there: a redemption that just landed, and a browser that was already signed in when the link
	// arrived. `state.kind` as the dependency, because a re-created `leave` must not re-navigate.
	useEffect(() => {
		if (state.kind === "signedIn") {
			goToRoot(history);
		}
	}, [state.kind, history]);

	const refusedFinish = state.kind === "refused" && state.from === "finish";
	// Memoised because the modal lists this object in an effect's dependencies: rebuilt every render it
	// would re-run that effect every render, for a value that changes only when the refusal does.
	//
	// A `linkUsed` is news, not a fault: the signup this link was for is already finished, and the code
	// form below is how they sign into it. Anything else genuinely went wrong.
	const entryNote: LoginModalNote | undefined = useMemo(
		() => (state.kind === "refused" && state.from === "finish"
			? {
				kind: state.refusal === "linkUsed" ? "success" : "error",
				text: state.message ?? login.code.signInFailed,
			}
			: undefined),
		[state, login.code.signInFailed],
	);

	return (
		<Shell>
			<PageBackdrop>
				<AuroraBackdrop/>
			</PageBackdrop>
			{state.kind === "none" || refusedFinish
				// MUI's trap is the sole focus owner; this route supplies no local exit callback.
				? (
					<LoginModal
						open
						onClose={leave}
						onSignedIn={leave}
						entryView={refusedFinish ? "code" : "signIn"}
						entryNote={entryNote}
					/>
				)
				: state.kind !== "signedIn" && (
					<SignupLinkCard state={state} onConfirm={confirm} onStop={stop}/>
				)}
		</Shell>
	);
}
